// AM-030: types for the routing operation AND/OR dependency graph.
// Mirrors backend/apps/production/routing/models.py (OperationJoinType,
// RoutingOperationDependency) and serializers_am030.py.

export type OperationJoinType = "AND" | "OR";

export interface RoutingOperationSummary {
  id: string;
  operationCode: string;
  operationName: string;
  sequenceNo: number;
  upstreamJoinType: OperationJoinType;
}

export interface RoutingOperationDependencyEdge {
  id: string;
  tenantId: string;
  routingRevision: string;
  upstreamOperation: string;
  downstreamOperation: string;
}

export interface CreateDependencyEdgeInput {
  routingRevision: string;
  upstreamOperation: string;
  downstreamOperation: string;
}

/**
 * Client-side mirror of RoutingDependencyGraphService.has_cycle — used only
 * for immediate UI feedback (disabling the "Add" button / showing a warning)
 * before the request round-trips to the backend, which remains the
 * authoritative validator (see AM-030 §3.2 Rule JOIN-05).
 */
export function wouldCreateCycle(
  edges: RoutingOperationDependencyEdge[],
  candidateUpstream: string,
  candidateDownstream: string
): boolean {
  if (candidateUpstream === candidateDownstream) return true;

  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    const edgeUp = (edge as any).upstream_operation ?? edge.upstreamOperation;
    const edgeDown = (edge as any).downstream_operation ?? edge.downstreamOperation;
    const list = adjacency.get(edgeUp) ?? [];
    list.push(edgeDown);
    adjacency.set(edgeUp, list);
  }
  const list = adjacency.get(candidateUpstream) ?? [];
  list.push(candidateDownstream);
  adjacency.set(candidateUpstream, list);

  const color = new Map<string, 0 | 1 | 2>();
  const allNodes = new Set<string>();
  adjacency.forEach((targets, source) => {
    allNodes.add(source);
    targets.forEach((t) => allNodes.add(t));
  });

  const visit = (node: string): boolean => {
    color.set(node, 1);
    for (const neighbor of adjacency.get(node) ?? []) {
      const c = color.get(neighbor) ?? 0;
      if (c === 1) return true;
      if (c === 0 && visit(neighbor)) return true;
    }
    color.set(node, 2);
    return false;
  };

  for (const node of allNodes) {
    if ((color.get(node) ?? 0) === 0) {
      if (visit(node)) return true;
    }
  }
  return false;
}

/**
 * Client-side mirror of services/readiness.is_ready_to_start — used for the
 * "preview mode" toggle in the graph editor, showing which operations would
 * be ready given a hypothetical set of completed operations. The real
 * decision during execution is made by ADR-102's Shopfloor Execution
 * backend, not by this function.
 */
export function isReadyToStart(
  operation: RoutingOperationSummary,
  upstreamOperationIds: string[],
  completedOperationIds: Set<string>
): boolean {
  if (upstreamOperationIds.length === 0) return true;
  if (operation.upstreamJoinType === "AND") {
    return upstreamOperationIds.every((id) => completedOperationIds.has(id));
  }
  return upstreamOperationIds.some((id) => completedOperationIds.has(id));
}
